import React, { useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import {
  objEmpty,
  objHasKey,
  useLocation,
  QUAM_QUOTE_COOKIE_KEY,
} from "../../Helpers";

import { useSpectraAnalytics } from "spectra/src/hooks/useSpectraAnalytics";

export default function AddToQuoteBtn({
  label = "Add",
  labelRemove = "Remove from Quote",
  product,
  quantity = 1,
  variation = "regular",
  version = undefined,
}) {
  // cookies
  const [cookies, setCookie] = useCookies([QUAM_QUOTE_COOKIE_KEY]);

  // states
  const [addedToQuote, setAddedToQuote] = useState(false);
  const [status, setStatus] = useState("idle"); // "idle" | "loading" | "success"

  const location = useLocation();
  const analytics = useSpectraAnalytics();

  useEffect(() => {
    if (
      !objEmpty(cookies[QUAM_QUOTE_COOKIE_KEY]) &&
      objHasKey(cookies[QUAM_QUOTE_COOKIE_KEY], product.id)
    ) {
      setAddedToQuote(true);
    } else {
      setAddedToQuote(false);
    }
  }, [cookies, product.id]);

  const handleToggleQuote = (e, shouldAddToQuote) => {
    e.preventDefault();
    e.stopPropagation();

    if (shouldAddToQuote) {
      // 1. Trigger loading animation state
      setStatus("loading");

      if (analytics) {
        analytics.logEvent("quote", {
          product: product.name,
          quantity: quantity,
          add: true,
        });
      }

      const newQuotes = {
        ...cookies[QUAM_QUOTE_COOKIE_KEY],
        [product.id]: {
          title: product.title,
          url: `${location.origin}/product/${product.name}`,
          quantity: quantity,
          ...{version},
        },
      };

      // Simulating a minor delay for the loading animation look & feel
      setTimeout(() => {
        setCookie(QUAM_QUOTE_COOKIE_KEY, newQuotes, { path: "/" });
        setAddedToQuote(true);
        setStatus("success");

        //window.scrollTo({ top: 0, behavior: "smooth" });

        setTimeout(() => {
          setStatus("idle");
        }, 2500);
      }, 600); // 600ms fake network delay for a nice dynamic UI bounce
    } else {
      // remove from quote
      if (analytics) {
        analytics.logEvent("quote", {
          product: product.name,
          remove: true,
        });
      }

      const newQuotes = { ...cookies[QUAM_QUOTE_COOKIE_KEY] };
      delete newQuotes[product.id];

      setCookie(QUAM_QUOTE_COOKIE_KEY, newQuotes, { path: "/" });
      setAddedToQuote(false);
      setStatus("idle");
    }
  };

  // Dynamically determine what label to show inside the button
  const renderButtonContent = () => {
    if (status === "loading") {
      return (
        <span className="flex items-center justify-center gap-2">
          {/* Loading Spinner */}
          <svg
            className="animate-spin h-5 w-5 text-white"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Adding...
        </span>
      );
    }

    if (status === "success") {
      return "Added to Quote!";
    }

    // Default states
    return addedToQuote ? labelRemove : label;
  };

  let className =
    "cursor-pointer text-lg text-white h-14 px-3 font-bold transition-colors duration-200";
  if (variation === "small") {
    className =
      "cursor-pointer text-sm text-white h-10 px-2 font-bold transition-colors duration-200";
  }
  if (variation === "compact") {
    className =
      "cursor-pointer text-sm text-white min-h-padding-3 px-2 font-bold transition-colors duration-200";
  }

  return (
    <button
      className={`${className} ${
        status === "success" ? "bg-blue" : "bg-error"
      }`}
      disabled={status === "loading"}
      onClick={(e) => handleToggleQuote(e, !addedToQuote)}
    >
      {renderButtonContent()}
    </button>
  );
}
